import PropTypes from 'prop-types';
import React from 'react';
import InlineItemList from 'components/pure/utils/InlineItemList';
import immutable from 'immutable';
import SelectField from 'components/pure/utils/form/SelectField';
import {componentHoc} from 'utils/mixin/componentHoc';
import ConstValues from 'utils/const';
import {distanceBetweenElementAndBottomOfWindow} from 'stores/distanceToBottom';

/**
 * MultipleSelectField consists of:
 * <div>
 *   <div className={list-container-classname}/>
 * </div>
 *
 * All MultipleSelectField should be implemented this way so that we have a consistent implementation pattern across the
 * whole web repo.
 *
 * @deprecated use @components/multi-select component instead
 *            You can take a look storybook or
 *            You can find usage example here (with immutable): components/pure/admin/SkillsSelectField.tsx
 */
@componentHoc
export default class MultipleSelectField extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      dropdownDirection: ConstValues.reactSelectize.dropdownDirection.down,
    };
  }

  _shouldRenderNoResults(search) {
    return search && search.trim().length > 0;
  }

  _clearUpdateDimensionsTimer() {
    if (this.updateDimensionsTimer) {
      window.clearTimeout(this.updateDimensionsTimer);
    }
  }

  _updateMultipleSelectFieldDropdownDirectionIfNecessary() {
    if (this._selectField) {
      const {up, down} = ConstValues.reactSelectize.dropdownDirection;
      const distance = distanceBetweenElementAndBottomOfWindow(this._selectField);
      if (distance < ConstValues.reactSelectize.dropdownHeight) {
        if (this.state.dropdownDirection !== up) {
          this.setState({dropdownDirection: up});
        }
      } else if (this.state.dropdownDirection !== down) {
        this.setState({dropdownDirection: down});
      }
    }
  }

  updateDimensions = () => {
    this._clearUpdateDimensionsTimer();
    this.updateDimensionsTimer = setTimeout(
      () => this._updateMultipleSelectFieldDropdownDirectionIfNecessary(),
      100
    );
  };

  handleSearchChange = search => {
    this.props.selectField.onSearchChange(search);
  };

  handleOpenChange = open => {
    this._updateMultipleSelectFieldDropdownDirectionIfNecessary();
    if (this.props.selectField.handleOpenChange) {
      this.props.selectField.handleOpenChange(open);
    }
  };

  renderNoResultsFound = () => {
    if (this._shouldRenderNoResults(this.props.selectField.search)) {
      return (
        <div className="no-results-found">
          {this.props.selectField.noResultsMessage ?? 'No Results found'}
        </div>
      );
    } else {
      return <div className="no-results-found">Type for suggestions...</div>;
    }
  };

  filterOptions = (options, search) => {
    const selectField = this.props.selectField;
    if (!!selectField && !!selectField.filterOptions) {
      return selectField.filterOptions(options, search);
    }
    if (search) {
      options = options.filter(option => {
        // Implement a complicated logic:
        // Return the options whose all of whose label's words has each search word as a prefix
        const searchWords = search.split(' ').filter(word => word !== '');
        const words = option.label.split(' ').filter(word => word !== '');
        let foundSearchWordAsPrefix = immutable.Set();
        words.forEach(word => {
          const found = searchWords.find(searchWord =>
            word.toLowerCase().startsWith(searchWord.toLowerCase())
          );
          if (found) {
            foundSearchWordAsPrefix = foundSearchWordAsPrefix.add(found);
          }
        });
        return (
          foundSearchWordAsPrefix.count() >= searchWords.length &&
          words.length >= searchWords.length
        );
      });
    }
    return options;
  };

  componentDidMount() {
    this._updateMultipleSelectFieldDropdownDirectionIfNecessary();
    window.addEventListener('resize', this.updateDimensions);
    window.addEventListener('scroll', this.updateDimensions);
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.updateDimensions);
    window.removeEventListener('scroll', this.updateDimensions);
    this._clearUpdateDimensionsTimer();
  }

  render() {
    const stateDropdownDirection = this.state.dropdownDirection;
    const selectField = this.props.selectField;
    const selectedList = this.props.selectedList;
    // Props override state -- state automatically decides direction otherwise.
    const dropdownDirection = selectField.dropdownDirection
      ? selectField.dropdownDirection
      : stateDropdownDirection;
    return (
      <div style={this.props.style}>
        <div className="search-field">
          {selectField.label && (
            <div className={selectField.label.className}>{selectField.label.label}</div>
          )}
          <SelectField
            ref={r => (this._selectField = r)}
            name={selectField.name}
            hideResetButton={true}
            className={selectField.className}
            placeholder={selectField.placeholder}
            options={selectField.options}
            tether={true}
            onBlurResetsInput={false}
            renderNoResultsFound={this.renderNoResultsFound}
            onOpenChange={this.handleOpenChange}
            onChange={selectField.onChange}
            onSearchChange={this.handleSearchChange}
            updateSearch={selectField.onSearchChange}
            search={selectField.search}
            value={selectField.value}
            dropdownDirection={dropdownDirection}
            filterOptions={this.filterOptions}
          />
        </div>
        {selectedList && (
          <div className={selectedList.containerClassname}>
            <InlineItemList
              itemList={selectedList.items}
              onRemove={item => this.props.removeItem(item)}
            />
          </div>
        )}
      </div>
    );
  }
}

MultipleSelectField.propTypes = {
  removeItem: PropTypes.func.isRequired,
  selectedList: PropTypes.shape({
    containerClassname: PropTypes.string.isRequired,
    listClassname: PropTypes.string.isRequired,
    spanClassname: PropTypes.string.isRequired,
    items: PropTypes.array,
  }),
  selectField: PropTypes.shape({
    handleOpenChange: PropTypes.func,
    label: PropTypes.shape({
      className: PropTypes.string,
      label: PropTypes.string.isRequired,
    }),
    className: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    placeholder: PropTypes.string,
    noResultsMessage: PropTypes.string,
    options: PropTypes.array.isRequired,
    onChange: PropTypes.func,
    onSearchChange: PropTypes.func,
    search: PropTypes.string,
    value: PropTypes.string,
    filterOptions: PropTypes.func,
    dropdownDirection: PropTypes.number,
  }),
};
